/* eslint-disable react-refresh/only-export-components */
/**
 * src/router.tsx
 * =============================================================================
 * Rutas de la app.
 *
 * Filosofía post-2026-05-26: "la pizarra ES la app". NO hay /app separado.
 * La landing pública y la pizarra del usuario logueado son la misma pantalla;
 * la diferencia visible es el chrome arriba-derecha (login/signup vs avatar).
 *
 * Code-splitting (2026-05-29): las páginas pesadas — las que importan
 * `pdfjs-dist` (~1 MB con el worker), pdf-lib o el modal de cuenta — se
 * cargan con `React.lazy`. El bundle inicial cae a la landing + chrome de
 * auth solamente. Suspense con fallback minimalista (sin spinner ruidoso)
 * para que el flash sea casi imperceptible cuando el chunk ya está cacheado.
 *
 * El `eslint-disable react-refresh/only-export-components` en cabecera es
 * necesario porque este archivo exporta `router` (no es componente) y los
 * componentes lazy a la vez. Mezclar rompe HMR — aquí no aplica porque
 * el router solo cambia al editar este archivo, no en runtime.
 *
 * Rutas (todas públicas — el drawer "Mis documentos" se abre dentro de
 * LandingPage con state, no es una página propia):
 *   /                       → LandingPage (drop-zone + auth-aware chrome)
 *   /login                  → LoginPage
 *   /signup                 → SignupPage
 *   /precios                → PreciosPage (modal-style sobre la pizarra)
 *   /s/:token               → SharePage (acceso anónimo por share link)
 *   /legal/...              → páginas legales
 *   /mis-documentos         → redirige a /?docs=1 (drawer auto-abre)
 *   /app y /app/*           → legacy, redirigen a /
 * =============================================================================
 */

import { Suspense, type ReactNode } from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { LandingPage } from '@/pages/LandingPage'
import { LoginPage } from '@/pages/LoginPage'
import { SignupPage } from '@/pages/SignupPage'
import { lazyPagina } from '@/lib/lazyPagina'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { PublicOnlyRoute } from '@/components/auth/PublicOnlyRoute'

// Páginas con visor PDF van diferidas: pesan de más y solo hacen falta tras
// navegar a /s o /d.
//
// Se usa `lazyPagina` y no el `lazy` de React: si algún día un fragmento no
// se puede cargar, recarga una vez en lugar de dejar la pantalla en blanco.
// NO es un fallo que tengamos hoy —Cloudflare Pages conserva los ficheros de
// despliegues anteriores, medido— sino una red. Ver `lib/lazyPagina.ts`.
const SharePage = lazyPagina(() =>
  import('@/pages/SharePage').then((m) => ({ default: m.SharePage })),
)
const DocumentoPage = lazyPagina(() =>
  import('@/pages/DocumentoPage').then((m) => ({ default: m.DocumentoPage })),
)
/**
 * Panel de operación. Diferido como el resto: lo abre una persona de vez en
 * cuando, no tiene sentido que viaje en el paquete de todos los visitantes.
 */
const AdminPage = lazyPagina(() =>
  import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
)
const PreciosPage = lazyPagina(() =>
  import('@/pages/PreciosPage').then((m) => ({ default: m.PreciosPage })),
)
const AcceptInvitePage = lazyPagina(() =>
  import('@/pages/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage })),
)
const AcceptSpaceInvitePage = lazyPagina(() =>
  import('@/pages/AcceptSpaceInvitePage').then((m) => ({
    default: m.AcceptSpaceInvitePage,
  })),
)
const ForgetPasswordPage = lazyPagina(() =>
  import('@/pages/ForgetPasswordPage').then((m) => ({ default: m.ForgetPasswordPage })),
)
const ResetPasswordPage = lazyPagina(() =>
  import('@/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
)
const TermsPage = lazyPagina(() =>
  import('@/pages/legal/TermsPage').then((m) => ({ default: m.TermsPage })),
)
const PrivacyPage = lazyPagina(() =>
  import('@/pages/legal/PrivacyPage').then((m) => ({ default: m.PrivacyPage })),
)
const CookiesPage = lazyPagina(() =>
  import('@/pages/legal/CookiesPage').then((m) => ({ default: m.CookiesPage })),
)

/**
 * Fallback minimalista — solo el fondo zinc-100 mientras se descarga el
 * chunk. Sin spinner para no parpadear cuando el chunk ya está cacheado
 * (que es el 99% de los casos tras la primera navegación). Si fuera
 * necesario más feedback, añadir un Loader2 tras 300ms con un timeout.
 */
function Loading({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<div className="fixed inset-0 bg-zinc-100" aria-hidden />}>
      {children}
    </Suspense>
  )
}

export const router = createBrowserRouter([
  { path: '/', element: <LandingPage /> },
  {
    path: '/login',
    element: (
      <PublicOnlyRoute>
        <LoginPage />
      </PublicOnlyRoute>
    ),
  },
  {
    path: '/signup',
    element: (
      <PublicOnlyRoute>
        <SignupPage />
      </PublicOnlyRoute>
    ),
  },
  // /mis-documentos legacy: redirige a /?docs=1 que LandingPage interpreta
  // para abrir el drawer lateral. Mantiene bookmarks viejos funcionales sin
  // tener una página entera duplicada.
  { path: '/mis-documentos', element: <Navigate to="/?docs=1" replace /> },
  // NO va envuelto en RequireAuth: quien no es superadmin debe ver "esta página
  // no existe", no un redirect a la pantalla de entrar — que le confirmaría que
  // hay algo detrás. El permiso lo decide el servidor, no esta tabla de rutas.
  { path: '/admin', element: <AdminPage /> },
  // Precios — pública. Si el user clicka "Hazte Pro" estando logueado pasa
  // directo a Polar Checkout. Si no, va a /signup primero.
  {
    path: '/precios',
    element: (
      <Loading>
        <PreciosPage />
      </Loading>
    ),
  },
  // Compat: cualquiera con bookmark a /app (era el viejo dashboard) acaba en /.
  { path: '/app', element: <Navigate to="/" replace /> },
  { path: '/app/*', element: <Navigate to="/" replace /> },
  // Share publico (sin login). El token es el id (UUID) de la fila en
  // `waitlist`. Cuando refactoricemos a `share_links` con PIN/caducidad,
  // mantenemos esta misma URL para no romper enlaces ya enviados.
  {
    path: '/s/:token',
    element: (
      <Loading>
        <SharePage />
      </Loading>
    ),
  },
  // Documento autenticado en Mi Espacio. Si no hay sesión, RequireAuth
  // redirige a /login y vuelve aquí tras el login. El visor es similar
  // al de share pero usa endpoints autenticados (/view-documento +
  // /version-annotations) y guarda anotaciones nominalmente (con autor_user_id).
  {
    path: '/d/:documentoId',
    element: (
      <RequireAuth>
        <Loading>
          <DocumentoPage />
        </Loading>
      </RequireAuth>
    ),
  },
  // Landing del magic link de invitación nominal a un documento.
  // Pública: si no hay sesión, ofrece CTAs signup/login; si hay sesión,
  // acepta auto y redirige a /d/<id>.
  {
    path: '/invite/accept',
    element: (
      <Loading>
        <AcceptInvitePage />
      </Loading>
    ),
  },
  // Aceptar invitación a un espacio compartido (multi-miembro). Mismo
  // patrón que /invite/accept pero para organizations.
  {
    path: '/invite/space',
    element: (
      <Loading>
        <AcceptSpaceInvitePage />
      </Loading>
    ),
  },
  // Reset de contraseña — flow en dos pasos: /forgot-password manda email,
  // /reset-password recibe el token del link del email.
  {
    path: '/forgot-password',
    element: (
      <Loading>
        <ForgetPasswordPage />
      </Loading>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Loading>
        <ResetPasswordPage />
      </Loading>
    ),
  },
  {
    path: '/legal/terminos',
    element: (
      <Loading>
        <TermsPage />
      </Loading>
    ),
  },
  {
    path: '/legal/privacidad',
    element: (
      <Loading>
        <PrivacyPage />
      </Loading>
    ),
  },
  {
    path: '/legal/cookies',
    element: (
      <Loading>
        <CookiesPage />
      </Loading>
    ),
  },
  { path: '*', element: <NotFoundPage /> },
])
